import {
  CloseCircleFilled,
  CloseCircleOutlined,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useContext, useMemo } from 'react';

import {
  CloseIconsForModalWithDataTestId,
  EmptyComponentWithDataTestId,
  Error403Unauthorized,
  MultipleSelect,
  TagsModal,
} from '@app/components';
import { CustomAlert } from '@app/components/CustomAlert';
import { DATE_PICKER_FORMAT, maxLength, minLength, OFF } from '@app/constants';
import { ERROR } from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { rangePickerLocale } from '@app/constants/rangePickerLocale';
import { RECORDS_SERVICE_HEADER, U_ACTION, U_KEY } from '@app/constants/tables';
import { HeaderContext } from '@app/providers';
import { FiltersAccordion, MultiSelect } from '@app/shared/components';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NRecords } from '@app/types';
import { divideAmount } from '@app/utils';

import { useRecords } from '@pages/Clients/Records/hooks/useRecords';
import { useRecordsEffects } from '@pages/Clients/Records/hooks/useRecordsEffects';

import { paymentStatusSelectOptions } from '@pages/Clients/Records/data/contants';
import { adaptDataToTable } from '@pages/Clients/Records/utils/adaptDataToTable';

import type { FC } from 'react';

import '@pages/Clients/Records/styles/RecordList.desktop.scss';
import '@pages/Clients/Records/styles/RecordList.tablet.scss';
import '@pages/Clients/Records/styles/RecordList.mobile.scss';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const RECORDS_TABLE_SCROLL_X = 1425;
const validationRules = {
  search: [minLength(), maxLength()],
};

export const RecordsList: FC = () => {
  const { isLoading, header: recordsProviderData } = useContext(HeaderContext);

  const records =
    recordsProviderData && Array.isArray(recordsProviderData.records)
      ? recordsProviderData.records
      : [];
  const pageNumber =
    recordsProviderData && typeof recordsProviderData.pageNumber === 'number'
      ? recordsProviderData.pageNumber
      : 1;
  const totalRowsCount =
    recordsProviderData &&
    typeof recordsProviderData.totalRowsCount === 'number'
      ? recordsProviderData.totalRowsCount
      : 0;
  const rowsPerPage =
    recordsProviderData && typeof recordsProviderData.rowsPerPage === 'number'
      ? recordsProviderData.rowsPerPage
      : 20;
  const searchId = recordsProviderData?.searchId as number | null;

  const {
    selectionCount,
    isLoadingData,
    handlePageChange,
    form,
    submitFormHandler,
    selectedEmployees,
    setSelectedEmployees,
    selectedUsers,
    setSelectedUsers,
    selectedStatuses,
    setSelectedStatuses,
    selectedCategories,
    setSelectedCategories,
    searchError,
    setSearchError,
    selectionType,
    selectedServices,
    setSelectedServices,
    handleReset,
    rowSelection,
    deletionLoader,
    isArchivingModalOpen,
    setIsArchivingModalOpen,
    deleteRows,
    tagsLoader,
    tagsModal,
    setTagsModal,
    updateTags,
    setSelectedTagsIds,
    selectedTagsIds,
    tags,
    toolbarClass,
    isMobile,
    MassActionButton,
    IsRegister,
    submitForm,
    getInitialTableData,
    selectedAdditionalSales,
    setSelectedAdditionalSales,
    employeesSelectOptions,
    usersSelectOptions,
    serviceCategoriesSelectOptions,
    servicesSelectOptions,
    onOpenRecord,
    onOpenRecordNewTab,
    clientStatusSelectOptions,
    additionalSalesSelectOptions,
    recordTagsMS,
    setRecordTagsMS,
    clientTagsMS,
    setClientTagsMS,
    recordTagsMsOptions,
    clientTagsMsOptions,
    setSelectedPaymentStatus,
    selectedPaymentStatus,
    disabledPastDate,
    recordTypesSelectOptions,
    setSelectedRecordTypes,
    selectedRecordTypes,
    getStatus,
  } = useRecords({
    records,
    pageNumber,
    totalRowsCount,
    rowsPerPage,
    searchId,
  });

  useRecordsEffects({
    isLoading,
    submitForm,
    getInitialTableData,
  });

  const recordsServiceColumns = useMemo(
    () =>
      RECORDS_SERVICE_HEADER.map((column) =>
        column.key === U_ACTION
          ? {
              ...column,
              fixed: 'right' as const,
              width: 64,
            }
          : {
              ...column,
              ellipsis: true,
            }
      ),
    []
  );

  if (!IsRegister) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}records`}>
      <div className="header-container">
        <FiltersAccordion>
          <Form
            form={form}
            autoComplete={OFF}
            onFinish={submitFormHandler}
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 30 }}
            style={{ marginBottom: 10 }}
            layout="vertical"
            initialValues={{
              DateCreate: [dayjs(), dayjs()],
            }}
          >
            <div className={toolbarClass}>
              <div className="date-container">
                <div className="date-item">
                  <Text
                    className="label"
                    strong
                    data-testid="creation-period-title"
                  >
                    Период создания:
                  </Text>
                  <Form.Item name="DateCreate">
                    <RangePicker
                      format={DATE_PICKER_FORMAT}
                      size="middle"
                      allowEmpty={[true, true]}
                      locale={rangePickerLocale}
                      allowClear={{
                        clearIcon: (
                          <CloseCircleFilled data-testid="creation-period-remove" />
                        ),
                      }}
                      disabledDate={disabledPastDate}
                    />
                  </Form.Item>
                </div>

                <div className="date-item">
                  <Text
                    className="label visits-period-label"
                    strong
                    data-testid="visit-period-title"
                  >
                    Период визита:
                  </Text>
                  <Form.Item name="DateVisit">
                    <RangePicker
                      format={DATE_PICKER_FORMAT}
                      size="middle"
                      allowEmpty={[true, true]}
                      locale={rangePickerLocale}
                      allowClear={{
                        clearIcon: (
                          <CloseCircleFilled data-testid="visit-period-remove" />
                        ),
                      }}
                      disabledDate={disabledPastDate}
                    />
                  </Form.Item>
                </div>
              </div>

              <Form.Item
                name="Search"
                wrapperCol={{ span: 16 }}
                rules={validationRules.search}
              >
                <Input
                  placeholder="Введите имя или телефон клиента"
                  addonBefore={<SearchOutlined />}
                  data-testid="client-search-input"
                />
              </Form.Item>

              <div className="selectors-container">
                <div className="selectors">
                  <MultipleSelect
                    className="user-selector"
                    placeholder="Все исполнители"
                    allOptions={employeesSelectOptions}
                    width={250}
                    minWidth={250}
                    selectedOption={selectedEmployees}
                    setSelectedOptions={setSelectedEmployees}
                    filterName="filterName"
                    dataTestId="all-executors-input"
                    dropdownDataTestId="executors-dropdown"
                    optionDataTestId="executor-option"
                  />

                  <MultipleSelect
                    className="user-selector"
                    placeholder="Все пользователи"
                    allOptions={usersSelectOptions}
                    width={250}
                    minWidth={250}
                    selectedOption={selectedUsers}
                    setSelectedOptions={setSelectedUsers}
                    filterName="filterName"
                    dataTestId="all-users-input"
                    dropdownDataTestId="users-dropdown"
                    optionDataTestId="user-item"
                  />

                  <MultipleSelect
                    className="user-selector"
                    placeholder="Все статусы записей"
                    allOptions={clientStatusSelectOptions}
                    width={250}
                    minWidth={250}
                    selectedOption={selectedStatuses}
                    setSelectedOptions={setSelectedStatuses}
                    dataTestId="all-records-statuses-input"
                    dropdownDataTestId="statuses-dropdown"
                    optionDataTestId="status-item"
                  />

                  <MultipleSelect
                    className="user-selector"
                    placeholder="Все категории услуг"
                    allOptions={serviceCategoriesSelectOptions}
                    width={250}
                    minWidth={250}
                    selectedOption={selectedCategories}
                    setSelectedOptions={setSelectedCategories}
                    filterName="filterName"
                    dataTestId="all-services-categories-input"
                    dropdownDataTestId="categories-dropdown"
                    optionDataTestId="category-item"
                  />

                  <MultipleSelect
                    className="user-selector"
                    placeholder="Все услуги"
                    allOptions={servicesSelectOptions}
                    width={250}
                    minWidth={250}
                    selectedOption={selectedServices}
                    setSelectedOptions={setSelectedServices}
                    filterName="filterName"
                    dataTestId="all-services-input"
                    dropdownDataTestId="services-dropdown"
                    optionDataTestId="service-item"
                  />

                  <MultiSelect
                    style={{ width: 250, minWidth: 250 }}
                    options={recordTagsMsOptions}
                    value={recordTagsMS}
                    onChange={setRecordTagsMS}
                    allowClear
                    placeholder="Все теги записей"
                    fixedInputLabel="Теги записей"
                    className="user-selector"
                    dataTestId="all-records-tags-input"
                    dropdownDataTestId="record-tags-dropdown"
                    optionDataTestId="record-tag-item"
                    activeCheckboxDataTestId="active-record-tag"
                    deactivateCheckboxDataTestId="record-tag-item"
                  />

                  <MultiSelect
                    style={{ width: 250, minWidth: 250 }}
                    options={clientTagsMsOptions}
                    value={clientTagsMS}
                    onChange={setClientTagsMS}
                    allowClear
                    placeholder="Все теги клиентов"
                    fixedInputLabel="Теги клиентов"
                    className="user-selector"
                    dataTestId="all-clients-tags-input"
                    dropdownDataTestId="client-tags-dropdown"
                    activeCheckboxDataTestId="active-client-tag"
                    deactivateCheckboxDataTestId="client-tag-item"
                  />

                  <MultipleSelect
                    className="user-selector"
                    placeholder="Допродажи"
                    allOptions={additionalSalesSelectOptions}
                    width={250}
                    minWidth={250}
                    selectedOption={selectedAdditionalSales}
                    setSelectedOptions={setSelectedAdditionalSales}
                    dataTestId="all-upsells-input"
                    dropdownDataTestId="upsells-dropdown"
                    optionDataTestId="upsell-item"
                  />

                  <Select<NRecords.ModelRecordSearchFilter['PaymentStatus']>
                    className="user-selector"
                    placeholder="Статус оплаты"
                    options={paymentStatusSelectOptions}
                    onChange={setSelectedPaymentStatus}
                    value={selectedPaymentStatus}
                    style={{ width: 250, minWidth: 250 }}
                    data-testid="payment-status-select"
                  />

                  <MultipleSelect
                    className="user-selector"
                    placeholder="Все типы записи"
                    allOptions={recordTypesSelectOptions}
                    width={250}
                    minWidth={250}
                    selectedOption={selectedRecordTypes}
                    setSelectedOptions={setSelectedRecordTypes}
                    dataTestId="all-record-types-input"
                    dropdownDataTestId="record-types-dropdown"
                    optionDataTestId="record-type-item"
                  />

                  <Form.Item
                    style={{
                      display: 'inline-block',
                      marginBottom: 0,
                    }}
                    className="buttons-container"
                  >
                    {searchError && (
                      <CustomAlert
                        errorType={ERROR}
                        serverError={searchError}
                        setServerError={setSearchError}
                      />
                    )}
                    <Space className="tablet-buttons" size="middle">
                      <Button
                        htmlType="reset"
                        onClick={handleReset}
                        className="button"
                        data-testid="reset-filters"
                      >
                        <CloseCircleOutlined />
                        Сброс
                      </Button>

                      <Button
                        type="primary"
                        htmlType="submit"
                        className="button"
                        data-testid="search-by-filters"
                      >
                        <FilterOutlined />
                        Поиск
                      </Button>
                    </Space>
                  </Form.Item>
                </div>

                <Form.Item
                  style={{
                    display: 'inline-block',
                    marginBottom: 0,
                  }}
                  className="buttons-container"
                >
                  {searchError && (
                    <CustomAlert
                      errorType={ERROR}
                      serverError={searchError}
                      setServerError={setSearchError}
                    />
                  )}
                  <Space className="desktop-buttons" size="middle">
                    <Button
                      htmlType="reset"
                      onClick={handleReset}
                      className="button"
                      data-testid="reset-filters"
                    >
                      <CloseCircleOutlined />
                      Сброс
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      className="button"
                      data-testid="search-by-filters"
                    >
                      <FilterOutlined />
                      Поиск
                    </Button>
                  </Space>
                </Form.Item>
              </div>
            </div>
          </Form>
        </FiltersAccordion>
      </div>
      <div className="records-count-container">
        <Typography.Text
          className="records-count"
          type="secondary"
          data-testid="chosen-operations-count"
        >
          {`Выбрано ${selectionCount} из ${divideAmount(
            totalRowsCount
          )} записей`}
        </Typography.Text>
        {MassActionButton}
      </div>
      <Table
        columns={recordsServiceColumns}
        loading={isLoadingData}
        dataSource={adaptDataToTable({
          records,
          onOpenRecord,
          onOpenRecordNewTab,
        })}
        locale={{ emptyText: <EmptyComponentWithDataTestId /> }}
        rowSelection={{
          type: selectionType,
          ...rowSelection,
        }}
        rowKey={(record) => String(record[U_KEY])}
        rowClassName={(record) =>
          `row-${String(record[U_KEY])} record-item row-status-${getStatus(
            record.Status.props.children
          )}`
        }
        pagination={false}
        scroll={{ x: RECORDS_TABLE_SCROLL_X }}
        size={isMobile ? 'small' : 'middle'}
        className="records-table"
      />

      <div className="pagination-container">
        <Pagination
          size={isMobile ? 'small' : 'default'}
          showSizeChanger={!isMobile}
          showLessItems={isMobile}
          responsive
          current={pageNumber}
          total={totalRowsCount}
          defaultCurrent={pageNumber}
          pageSize={rowsPerPage}
          onChange={handlePageChange}
          showTotal={(total, range) =>
            `Показаны ${range[0]}-${range[1]} из ${total}`
          }
        />
        {MassActionButton}
      </div>

      <Modal
        title={generateSpanWithDataTestId(
          `Удалить из базы записи (${selectionCount})`,
          'delete-title'
        )}
        data-testid="delete-popup"
        open={isArchivingModalOpen}
        cancelText="Отмена"
        okText="В архив"
        okType="danger"
        onOk={deleteRows}
        okButtonProps={{
          type: 'primary',
          loading: deletionLoader,
          'data-testid': 'confirm-delete',
        }}
        cancelButtonProps={{
          loading: deletionLoader,
          'data-testid': 'cancel-delete',
        }}
        onCancel={() => setIsArchivingModalOpen(false)}
        closeIcon={<CloseIconsForModalWithDataTestId />}
      >
        <p data-testid="delete-description">
          Вы уверены, что хотите удалить из базы записи ({selectionCount})?
        </p>
      </Modal>

      <TagsModal
        isTagsModalOpen={tagsModal}
        updateTags={updateTags}
        tagsLoader={tagsLoader}
        setIsTagsModalOpen={setTagsModal}
        setSelectedTagsIds={setSelectedTagsIds}
        selectionType={selectionType}
        selectedTagsIds={selectedTagsIds}
        tags={tags}
      />
    </div>
  );
};
